import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle2, Phone, MessageCircle, Mail, Loader2, CalendarCheck } from "lucide-react";
import { PROPERTY_TYPES, waLink } from "@/config/siteConfig";
import { useSiteSettings, getPhones, getWaNumber, getEmail, telHref } from "@/lib/siteSettings";
import { toast } from "@/components/ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTERESTS = ["Buy", "Rent", "Sell", "Investment"];
const TIME_SLOTS = ["10 AM – 12 PM", "12 – 2 PM", "2 – 4 PM", "4 – 6 PM"];

const inputCls =
  "h-12 w-full rounded-lg border border-line bg-warm/60 px-3.5 text-[14px] font-medium text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold transition";
const labelCls = "block text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-soft mb-2";

const EMPTY = { name: "", phone: "", interest: "", propertyType: "", location: "", budget: "", message: "" };

export const LeadSection = ({ prefill }) => {
  const { site } = useSiteSettings();
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [enquiryId, setEnquiryId] = useState(null);
  const [visitDate, setVisitDate] = useState("");
  const [visitTime, setVisitTime] = useState("");
  const [visitSaving, setVisitSaving] = useState(false);
  const [visitBooked, setVisitBooked] = useState(false);

  const phones = getPhones(site);
  const waNumber = getWaNumber(site);
  const email = getEmail(site);
  const heading = site?.contactHeading || "Tell Us What You're Looking For.";
  const desc =
    site?.contactDesc ||
    "Share your requirement and our team can help you explore suitable property options.";

  useEffect(() => {
    if (prefill) {
      setDone(false);
      setVisitBooked(false);
      setVisitDate("");
      setVisitTime("");
      setForm((f) => ({
        ...f,
        interest: prefill.interest || f.interest,
        message: prefill.message !== undefined ? prefill.message : f.message,
      }));
    }
  }, [prefill]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Please enter your full name.");
    if (!/^[0-9+\-\s()]{8,16}$/.test(form.phone.trim()))
      return toast.error("Please enter a valid phone number.");
    if (!form.interest) return toast.error("Please select what you're interested in.");
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/enquiries`, {
        ...form,
        name: form.name.trim(),
        phone: form.phone.trim(),
      });
      setEnquiryId(data.id);
      setDone(true);
      toast.success("Thank you! We've received your enquiry.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const bookVisit = async () => {
    if (!visitDate) return toast.error("Please pick a visit date.");
    if (!visitTime) return toast.error("Please pick a time slot.");
    setVisitSaving(true);
    try {
      await axios.patch(`${API}/enquiries/${enquiryId}/visit`, { visitDate, visitTime });
      setVisitBooked(true);
      toast.success("Site visit preference saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the visit. Please try again.");
    } finally {
      setVisitSaving(false);
    }
  };

  const waMsg = `Hi RK Real Estate, I'm ${form.name || "interested in a property"}. I'm looking to ${form.interest || "buy"}${
    form.location ? ` in ${form.location}` : ""
  }. Please help me with suitable options.`;

  return (
    <section id="enquire" className="bg-navy py-16 sm:py-24 lg:py-28" data-testid="lead-section">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        <div className="lg:sticky lg:top-28">
          <Reveal>
            <span className="inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.24em] text-gold">
              <span className="h-px w-8 bg-gold" /> Let's Find Your Property
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-[2.8rem] font-semibold text-white leading-[1.15] tracking-tight" data-testid="lead-heading">
              {heading}
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-5 text-sm sm:text-base lg:text-lg text-white/65 leading-relaxed max-w-md whitespace-pre-line" data-testid="lead-desc">
              {desc}
            </p>
          </Reveal>
          <Reveal delay={0.22}>
            <div className="mt-7 flex gap-2.5">
              {["Buy", "Rent", "Sell"].map((t) => (
                <span key={t} className="border border-gold/50 text-gold text-[12px] font-semibold tracking-[0.14em] uppercase rounded-md px-4 py-2">
                  {t}
                </span>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 space-y-4">
              {phones.map((p, i) => (
                <a key={p.id} href={telHref(p.number)} className="flex items-center gap-3.5 text-white/80 hover:text-gold transition-colors group w-fit" data-testid={i === 0 ? "lead-phone-link" : `lead-phone-link-${i + 1}`}>
                  <span className="h-10 w-10 rounded-lg border border-white/15 flex items-center justify-center group-hover:border-gold transition-colors">
                    <Phone size={16} className="text-gold" />
                  </span>
                  <span className="text-sm font-medium">{p.number}</span>
                </a>
              ))}
              <a href={waLink(waMsg, waNumber)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3.5 text-white/80 hover:text-gold transition-colors group w-fit" data-testid="lead-whatsapp-link">
                <span className="h-10 w-10 rounded-lg border border-white/15 flex items-center justify-center group-hover:border-gold transition-colors">
                  <MessageCircle size={16} className="text-gold" />
                </span>
                <span className="text-sm font-medium">Chat with us on WhatsApp</span>
              </a>
              <a href={`mailto:${email}`} className="flex items-center gap-3.5 text-white/80 hover:text-gold transition-colors group w-fit" data-testid="lead-email-link">
                <span className="h-10 w-10 rounded-lg border border-white/15 flex items-center justify-center group-hover:border-gold transition-colors">
                  <Mail size={16} className="text-gold" />
                </span>
                <span className="text-sm font-medium">{email}</span>
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="bg-white rounded-xl p-6 sm:p-9 shadow-[0_30px_80px_rgba(0,0,0,0.35)]" data-testid="lead-form-card">
            {done ? (
              <div className="text-center py-10" data-testid="lead-form-success">
                <span className="mx-auto h-16 w-16 rounded-full bg-gold/15 flex items-center justify-center">
                  <CheckCircle2 size={32} className="text-gold" />
                </span>
                <h3 className="mt-6 font-display text-2xl sm:text-3xl font-semibold text-navy">Thank You!</h3>
                <p className="mt-3 text-sm sm:text-base text-ink-soft leading-relaxed max-w-sm mx-auto">
                  We've received your enquiry. Our team will get in touch with you shortly.
                </p>

                {visitBooked ? (
                  <p className="mt-6 inline-flex items-center gap-2 text-[13px] font-semibold text-navy bg-gold/15 border border-gold/40 rounded-lg px-4 py-2.5" data-testid="visit-booked-confirmation">
                    <CalendarCheck size={15} className="text-gold-dark" />
                    Site visit requested: {visitDate} · {visitTime}
                  </p>
                ) : (
                  <div className="mt-7 text-left border border-line rounded-lg p-5" data-testid="site-visit-picker">
                    <p className="text-[12px] font-bold uppercase tracking-[0.14em] text-navy">Book a Site Visit <span className="text-ink-soft font-medium normal-case tracking-normal">(optional)</span></p>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input
                        type="date"
                        min={new Date().toISOString().split("T")[0]}
                        value={visitDate}
                        onChange={(e) => setVisitDate(e.target.value)}
                        data-testid="visit-date-input"
                        className="h-11 w-full rounded-lg border border-line bg-warm/60 px-3.5 text-[14px] font-medium text-ink focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold transition"
                      />
                      <Select value={visitTime} onValueChange={setVisitTime}>
                        <SelectTrigger className="h-11 w-full rounded-lg border-line bg-warm/60 text-[14px] font-medium text-ink focus:ring-1 focus:ring-gold" data-testid="visit-time-select">
                          <SelectValue placeholder="Time slot" />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_SLOTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <button
                      onClick={bookVisit}
                      disabled={visitSaving}
                      data-testid="book-visit-button"
                      className="mt-3 w-full bg-navy text-white hover:bg-navy-soft disabled:opacity-60 transition-colors rounded-lg px-6 py-3 text-[14px] font-semibold flex items-center justify-center gap-2"
                    >
                      {visitSaving ? <Loader2 size={16} className="animate-spin" /> : <><CalendarCheck size={15} /> Book Site Visit</>}
                    </button>
                  </div>
                )}

                <a
                  href={waLink(waMsg, waNumber)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="lead-success-whatsapp-button"
                  className="mt-7 w-full bg-[#25D366] hover:bg-[#20ba59] text-white transition-colors rounded-lg px-6 py-3.5 text-[15px] font-semibold inline-flex items-center justify-center gap-2"
                >
                  <MessageCircle size={17} /> Continue on WhatsApp
                </a>
                <button
                  onClick={() => { setForm(EMPTY); setDone(false); setVisitBooked(false); setVisitDate(""); setVisitTime(""); }}
                  data-testid="lead-form-another-button"
                  className="mt-3 w-full border border-line text-ink-soft hover:border-gold hover:text-navy transition-colors rounded-lg px-6 py-3 text-[14px] font-medium"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="lead-form">
                <h3 className="font-display text-xl sm:text-2xl font-semibold text-navy">Get Property Assistance</h3>
                <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelCls} htmlFor="lead-name">Full Name*</label>
                    <input id="lead-name" data-testid="lead-form-name-input" className={inputCls} placeholder="Enter your name"
                      value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="lead-phone">Phone Number*</label>
                    <input id="lead-phone" data-testid="lead-form-phone-input" className={inputCls} placeholder="Enter your phone number"
                      value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  </div>
                  <div>
                    <label className={labelCls}>I'm Interested In*</label>
                    <Select value={form.interest} onValueChange={(v) => setForm({ ...form, interest: v })}>
                      <SelectTrigger className={`${inputCls} px-3.5`} data-testid="lead-form-interest-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {INTERESTS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className={labelCls}>Property Type</label>
                    <Select value={form.propertyType} onValueChange={(v) => setForm({ ...form, propertyType: v })}>
                      <SelectTrigger className={`${inputCls} px-3.5`} data-testid="lead-form-property-type-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="lead-location">Preferred Location</label>
                    <input id="lead-location" data-testid="lead-form-location-input" className={inputCls} placeholder="Enter preferred location"
                      value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="lead-budget">Budget</label>
                    <input id="lead-budget" data-testid="lead-form-budget-input" className={inputCls} placeholder="Enter your budget"
                      value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="lead-message">Message</label>
                    <textarea id="lead-message" data-testid="lead-form-message-input" rows={4}
                      placeholder="Tell us what you're looking for..."
                      className="w-full rounded-lg border border-line bg-warm/60 px-3.5 py-3 text-[14px] font-medium text-ink placeholder:text-ink-soft/60 focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold transition resize-none"
                      value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="lead-form-submit-button"
                  className="mt-7 w-full bg-gold text-navy hover:bg-gold-dark hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 transition-all duration-300 rounded-lg px-6 py-4 text-[15px] font-semibold flex items-center justify-center gap-2 shadow-md shadow-gold/25"
                >
                  {submitting ? <Loader2 size={17} className="animate-spin" /> : <>Get Property Assistance <ArrowRight size={16} /></>}
                </button>
                <p className="mt-4 text-center text-[12px] text-ink-soft">
                  We'll use your details only to respond to your property enquiry.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
